/**
 * Composes a `PropertyView` from existing entities. Pure / no I/O.
 *
 * See `src/types/PropertyView.ts` for field semantics.
 */

import { differenceInCalendarDays } from 'date-fns';

import { USER_LOAN_PROGRAM, USER_QUALIFY_STATUS } from '@/config/constants';
import type { ContractPipelineResponse } from '@/apis/extended/pipelineApi';
import { getLiveDaysOnMarket } from '@/utils/listingDaysOnMarket';
import type { Contract } from '@/types/ContractType';
import type {
  LoanApplication,
  LoanProductType,
} from '@/types/LoanApplicationType';
import type { Product } from '@/types/ProductType';
import type {
  BuildPropertyViewInput,
  LoanTypeView,
  OfferStatus,
  PropertyStage,
  PropertyView,
  StageHistoryEntry,
} from '@/types/PropertyView';
import type { User } from '@/types/UserType';
import {
  closedAtStore,
  idleDismissedStore,
  stageEnteredStore,
  toursStore,
} from './localStateStore';
import {
  resolveSellerStage,
  sellerInfoComplete,
  visibleStagesForSeller,
} from './sellerStageResolver';
import {
  countApplicationCompletedSections,
  countApplicationSections,
  resolveStage,
  visibleStagesFor,
} from './stageResolver';

const IDLE_THRESHOLD_DAYS = 5;
const GALLERY_CAP = 5;

function galleryUrlsFromProduct(product: Product, max = GALLERY_CAP): string[] {
  const raw: string[] = [];
  const push = (u: string | undefined) => {
    const t = typeof u === 'string' ? u.trim() : '';
    if (t) raw.push(t);
  };
  push(product.photo);
  if (Array.isArray(product.photos)) {
    for (const p of product.photos) push(p);
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of raw) {
    if (seen.has(u)) continue;
    seen.add(u);
    out.push(u);
    if (out.length >= max) break;
  }
  return out;
}

function joinAddress(product: Product): {
  full: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  county?: string;
} {
  const a = product.address ?? {};
  const refi = product.refinance?.property;
  const merged = {
    street: a.street ?? refi?.street,
    city: a.city ?? refi?.city,
    state: a.state ?? refi?.state,
    zip: a.zip ?? refi?.zip,
    fullAddress: a.fullAddress ?? refi?.fullAddress,
    county: a.county ?? refi?.county,
  };
  const full =
    merged.fullAddress ||
    [merged.street, merged.city, merged.state, merged.zip]
      .filter(Boolean)
      .join(', ');
  return {
    full,
    street: merged.street,
    city: merged.city,
    state: merged.state,
    zip: merged.zip,
    county: merged.county,
  };
}

function loanProgramFor(
  application: LoanApplication | undefined | null,
  user: User | undefined | null,
  product: Product
): string | undefined {
  const fromApp = application?.productType;
  if (fromApp) return fromApp;
  const fromProduct = product.savedLoanType?.toLowerCase();
  if (fromProduct) return fromProduct;
  return user?.loanProgram;
}

function mapLoanType(
  program: string | undefined,
  isRefinance: boolean
): LoanTypeView {
  const p = (program ?? '').toLowerCase();
  const prefix = isRefinance ? 'refinance' : 'purchase';
  switch (p) {
    case USER_LOAN_PROGRAM.CONVENTIONAL:
    case 'conventional':
      return `${prefix}_conventional` as LoanTypeView;
    case USER_LOAN_PROGRAM.FHA:
    case 'fha':
      return `${prefix}_fha` as LoanTypeView;
    case USER_LOAN_PROGRAM.VA:
    case 'va':
      return `${prefix}_va` as LoanTypeView;
    case USER_LOAN_PROGRAM.USDA:
    case 'usda':
      return `${prefix}_usda` as LoanTypeView;
    case USER_LOAN_PROGRAM.DSCR:
    case 'dscr':
      return `${prefix}_dscr` as LoanTypeView;
    case USER_LOAN_PROGRAM.BRIDGE:
    case 'bridge':
      return `${prefix}_bridge` as LoanTypeView;
    case USER_LOAN_PROGRAM.HELOC:
      return 'refinance_heloc';
    case USER_LOAN_PROGRAM.HELOAN:
      return 'refinance_heloan';
    case USER_LOAN_PROGRAM.SECOND_MORTGAGE:
      return 'refinance_second_mortgage';
    default:
      return (
        isRefinance ? 'refinance_other' : 'purchase_other'
      ) as LoanTypeView;
  }
}

function computeStageEnteredAt(
  product: Product,
  stage: PropertyStage,
  application: LoanApplication | null | undefined,
  contract: Contract | null | undefined
): string {
  // 1. localStorage override
  const stored = stageEnteredStore.get(product._id, stage);
  if (stored) return stored;

  // 2. derive from underlying entity timestamps for the active stage
  switch (stage) {
    case 1:
      return product.createdAt;
    case 2:
    case 3:
      return (
        application?.updatedAt ?? application?.createdAt ?? product.updatedAt
      );
    case 4:
      return toursStore.list(product._id)[0]?.loggedAt ?? product.updatedAt;
    case 5:
    case 6:
    case 7:
    case 8: {
      const last = contract?.statusHistory?.[contract.statusHistory.length - 1];
      if (last?.timestamp) return String(last.timestamp);
      return contract?.updatedAt ?? contract?.createdAt ?? product.updatedAt;
    }
    case 9:
      return (
        closedAtStore.get(product._id) ??
        contract?.ratifiedAt ??
        contract?.updatedAt ??
        product.updatedAt
      );
    default:
      return product.updatedAt;
  }
}

function buildStageHistory(
  product: Product,
  contract: Contract | null | undefined,
  application: LoanApplication | null | undefined
): StageHistoryEntry[] {
  // Lightweight: we only know transitions reliably from contract.statusHistory.
  const entries: StageHistoryEntry[] = [
    { stage: 1, enteredAt: product.createdAt },
  ];
  if (application?.createdAt) {
    entries.push({ stage: 2, enteredAt: application.createdAt });
  }
  if (contract?.statusHistory?.length) {
    contract.statusHistory.forEach((h) => {
      const stage = statusToStage(h.status);
      if (stage) {
        entries.push({ stage, enteredAt: String(h.timestamp) });
      }
    });
  }
  return entries;
}

function statusToStage(status: string): PropertyStage | undefined {
  switch (status) {
    case 'draft':
      return 5;
    case 'negotiating':
    case 'awaiting_signatures':
    case 'awaiting_buyer_signature':
    case 'awaiting_seller_signature':
      return 6;
    case 'ratified':
    case 'active':
    case 'inspection_pending':
      return 7;
    case 'escrow':
    case 'completed':
      return 9;
    default:
      return undefined;
  }
}

function computeOfferStatus(
  contract: Contract | null | undefined
): OfferStatus {
  if (!contract) return null;
  if (contract.contractStatus === 'voided') return 'withdrawn';
  if (contract.isRatified || contract.contractStatus === 'ratified')
    return 'accepted';
  if (
    contract.contractStatus === 'negotiating' ||
    contract.contractStatus === 'repair_negotiating'
  )
    return 'countered';
  if (contract.contractStatus === 'draft') return 'pending';
  return 'pending';
}

function extractContractId(product: Product): string | undefined {
  if (!product.contract) return undefined;
  if (typeof product.contract === 'string') return product.contract;
  return product.contract._id;
}

function computeUwCounts(application: LoanApplication | null | undefined) {
  if (!application?.checklist) {
    return { total: undefined, open: undefined };
  }
  const required = application.checklist.filter((c) => c.required);
  const open = required.filter((c) => !c.completed).length;
  return { total: required.length, open };
}

export function buildPropertyView(input: BuildPropertyViewInput): PropertyView {
  const {
    product,
    application,
    contract,
    pipeline,
    user,
    pipelineMode = 'buyer',
    contractIdOverride,
    openAddendumCount = 0,
  } = input;
  const isSeller = pipelineMode === 'seller';
  const isRefinance = !isSeller && product.transactionMode === 'refinance';
  const program = loanProgramFor(application, user, product);
  const loanType = mapLoanType(program, isRefinance);
  const isDscr = !isSeller && loanType.includes('dscr');
  const address = joinAddress(product);
  const stage = isSeller
    ? resolveSellerStage({
        contract: contract ?? null,
        openAddendumCount,
        pipelineStageKey: pipeline?.data?.currentStageKey?.toLowerCase(),
      })
    : resolveStage({
        product,
        application: application ?? null,
        contract: contract ?? null,
        pipeline: pipeline ?? null,
        user: user ?? null,
        isRefinance,
      });
  const visibleStages = isSeller
    ? visibleStagesForSeller()
    : visibleStagesFor(isRefinance);
  const stageEnteredAt = computeStageEnteredAt(
    product,
    stage,
    application ?? null,
    contract ?? null
  );
  const daysInCurrentStage = Math.max(
    0,
    differenceInCalendarDays(new Date(), new Date(stageEnteredAt))
  );
  const isIdle =
    daysInCurrentStage >= IDLE_THRESHOLD_DAYS &&
    !idleDismissedStore.has(product._id);

  const closedAt = closedAtStore.get(product._id);
  const isClosed = isSeller
    ? stage === 7 &&
      Boolean(closedAt || contract?.contractStatus === 'completed')
    : stage === 9 && Boolean(closedAt);

  const tours = toursStore.list(product._id);
  const tourCount = tours.length;

  const contractId =
    contractIdOverride ??
    extractContractId(product) ??
    (contract?._id ? String(contract._id) : undefined);
  const offerStatus = computeOfferStatus(contract ?? null);
  const uwCounts = computeUwCounts(application ?? null);

  const dscrFields = application?.dscrFields;
  const monthlyRent = dscrFields?.monthlyRent ?? product.loanInfo?.rent;
  const dscrRatio = dscrFields?.computed?.dscr ?? undefined;

  const sectionsTotal = application
    ? countApplicationSections(application)
    : undefined;
  const sectionsComplete = application
    ? countApplicationCompletedSections(application)
    : undefined;

  return {
    id: product._id,
    productId: product._id,
    pipelineMode,
    address: address.full,
    street: address.street,
    city: address.city,
    state: address.state,
    zip: address.zip,
    county: address.county,
    propertyType: product.propertyType,
    beds: product.bedrooms,
    baths: product.bathrooms,
    sqft: product.sqft,
    yearBuilt: product.yearBuilt,
    lotSizeSqft: product.lotSize,
    imageUrl: product.photo || product.photos?.[0],
    galleryImageUrls: galleryUrlsFromProduct(product),

    listPrice: product.price ?? product.loanInfo?.offerPrice ?? 0,
    listingStatus: undefined,
    daysOnMarket: getLiveDaysOnMarket(product),
    daysOnMarketRecordedAt: product.daysOnMarketRecordedAt,
    zoning: product.zoning,
    priceReductions: (() => {
      const changes = product.priceChanges;
      if (!changes?.length) return undefined;
      const cuts = changes.filter((e) =>
        /cut|reduc|drop|decrease/i.test(e.event ?? '')
      );
      return cuts.length > 0 ? cuts.length : changes.length;
    })(),
    mlsNumber: undefined,

    loanType,
    isRefinance,
    isDscr,
    loanProgram: program,

    stage,
    visibleStages,
    stageEnteredAt,
    daysInCurrentStage,
    isIdle,
    stageHistory: buildStageHistory(
      product,
      contract ?? null,
      application ?? null
    ),

    loanAmount: application?.agencyFields?.loanAmount ?? undefined,
    downPaymentPct: undefined,
    downPaymentAmt: product.savedDownPayment ?? user?.loanInfo?.downPayment,
    interestRate: application?.dscrFields?.interestRateAnnualPct ?? undefined,
    loanTermYears:
      application?.agencyFields?.loanTermYears ??
      user?.loanInfo?.loanTerm ??
      undefined,
    estimatedMonthlyPayment: undefined,
    estimatedPITIA: undefined,

    monthlyRentEstimate: monthlyRent ?? undefined,
    dscrRatio: dscrRatio ?? undefined,
    monthlyCashFlow: undefined,
    annualTaxes: dscrFields?.monthlyTaxes
      ? Number(dscrFields.monthlyTaxes) * 12
      : undefined,
    annualInsurance: dscrFields?.monthlyInsurance
      ? Number(dscrFields.monthlyInsurance) * 12
      : undefined,
    monthlyHoa: dscrFields?.monthlyHoa ?? undefined,

    applicationId: application?._id
      ? String(application._id).trim() || undefined
      : undefined,
    applicationStatus: application?.status,
    applicationLifecycleStage: application?.lifecycleStage,
    applicationProductType: application?.productType as
      | LoanProductType
      | undefined,
    applicationSectionsComplete: sectionsComplete,
    applicationSectionsTotal: sectionsTotal,
    checklistRequiredTotal: uwCounts.total,
    checklistRequiredCompleted:
      uwCounts.total !== undefined && uwCounts.open !== undefined
        ? uwCounts.total - uwCounts.open
        : undefined,

    contractId,
    hasStartedContract: Boolean(product.contractStarted && product.contract),
    suggestedOfferPrice: undefined,
    bargainingPowerScore: undefined,
    offerSubmittedPrice: contract?.offerSpecifics?.offerPrice ?? undefined,
    offerSubmittedAt: contract?.createdAt,
    latestCounteroffer: undefined,
    offerStatus,
    contractStatus: contract?.contractStatus,
    contractRatified: contract?.isRatified,
    contractStatusHistory: contract?.statusHistory,

    pipelineCurrentStageKey: pipeline?.data?.currentStageKey,
    pipelineStages: pipeline?.data?.stages,
    processingChecklistComplete:
      uwCounts.open !== undefined ? uwCounts.open === 0 : undefined,
    processingChecklistPct:
      uwCounts.total && uwCounts.total > 0
        ? (uwCounts.total - (uwCounts.open ?? 0)) / uwCounts.total
        : undefined,
    uwConditionsOpen: uwCounts.open,
    uwConditionsTotal: uwCounts.total,
    estimatedClearToCloseDate: undefined,

    closingDate: contract?.offerSpecifics?.closingDate,
    closingLocation: undefined,
    cashToClose: undefined,
    closedAt,
    isClosed,

    tourLogged: tourCount > 0,
    tourCount,

    sellerDetailsComplete: isSeller
      ? sellerInfoComplete(contract ?? null)
      : undefined,
  };
}

/** True if the user (just) qualifies (income + credit + dp set + qualifyStatus). */
export function userIsQualified(user: User | undefined | null): boolean {
  if (!user) return false;
  return user.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY;
}
